/**
 * Section « Compte » des réglages (§17, étape 10).
 *
 * L'auth est **toujours optionnelle** (§3) : le joueur anonyme a déjà un vrai
 * `auth.uid()` et sa ville se synchronise sans rien demander. Cette section ne sert
 * qu'à **attacher** un e-mail à cette identité — `updateUser({ email })` convertit le
 * compte anonyme en compte permanent, même ville, même slug, zéro migration.
 *
 * C'est aussi pour ça qu'il n'y a PAS de bouton de déconnexion tant que le compte est
 * anonyme : le jeton local est la seule clé de la ville. S'en déconnecter, ce serait
 * l'abandonner — un bouton qui détruit sans le dire n'a pas sa place dans un écran de
 * réglages.
 */

import { FEATURES } from '@/config/features';
import { shouldRecordConsent } from '@/core/newsletter';
import { t } from '@/i18n';
import { fetchNewsletterConsent, recordNewsletterConsent } from '@/net/newsletter';
import { getSupabase, isSupabaseConfigured } from '@/net/supabase';
import { createButton } from './components';

export interface AccountSectionHandle {
  readonly el: HTMLElement;
  /** Recharge l'état (anonyme / relié) depuis la session. */
  refresh(): void;
  /**
   * Un e-mail est-il attaché à ce compte ?
   *
   * Rempli par `refresh()`, donc **faux tant qu'aucune lecture n'a eu lieu** — et c'est
   * le bon défaut : dans le doute, on propose une fois de trop plutôt que de priver un
   * joueur anonyme de la seule chose qui protège sa ville.
   */
  isLinked(): boolean;
  destroy(): void;
}

export function createAccountSection(): AccountSectionHandle {
  const el = document.createElement('div');
  el.className = 'account-section';

  const title = document.createElement('h4');
  title.className = 'account-section__title';
  title.textContent = t('account.title');

  const status = document.createElement('p');
  status.className = 'account-section__status';

  const form = document.createElement('form');
  form.className = 'account-section__form';

  const email = document.createElement('input');
  email.type = 'email';
  email.required = true;
  email.autocomplete = 'email';
  email.className = 'account-section__email';
  email.placeholder = t('account.emailPlaceholder');
  email.setAttribute('aria-label', t('account.emailPlaceholder'));

  const send = createButton({
    label: t('account.sendLink'),
    variant: 'primary',
    icon: 'social',
  });
  send.el.setAttribute('type', 'submit');

  form.append(email, send.el);

  /*
    ── LA CASE NEWSLETTER (ROADMAP 6.3) ───────────────────────────────────────

    ⚠️ **Décochée par construction, et il n'existe aucun chemin qui la coche seul.**
    L'art. 4.11 du RGPD exige un « acte positif clair » : une case pré-cochée, ou cochée
    par déduction depuis l'inscription, rend l'ensemble des envois illicites. La seule
    ligne qui la coche sans geste du joueur est celle qui RELIT l'état du serveur — donc
    un consentement déjà donné.

    Elle est **séparée du bouton d'envoi** : relier son e-mail pour sauver sa ville et
    accepter de recevoir des messages sont deux volontés distinctes. La phrase d'aide le
    dit à l'écran plutôt que de le laisser deviner.
  */
  const consentRow = document.createElement('label');
  consentRow.className = 'account-section__consent';

  const consent = document.createElement('input');
  consent.type = 'checkbox';
  consent.checked = false;
  consent.className = 'account-section__consent-box';

  const consentText = document.createElement('span');
  consentText.textContent = t('account.newsletter');
  consentRow.append(consent, consentText);

  const consentHint = document.createElement('p');
  consentHint.className = 'account-section__consent-hint';
  consentHint.textContent = t('account.newsletterHint');

  el.append(title, status, form);
  /*
    Voir `features.ts` : la case attend la migration de `newsletter_consents` sur le
    projet distant. Livrée visible, chaque tap afficherait « impossible d'enregistrer ton
    choix » — un défaut à l'écran pour une feature par ailleurs terminée.
  */
  if (FEATURES.newsletterConsent) el.append(consentRow, consentHint);

  let busy = false;
  let isLinked = false;
  /** Dernier état CONFIRMÉ par le serveur — jamais ce que la case affiche. */
  let consentState = false;
  let consentBusy = false;

  /**
   * Consigne un acte de volonté, et n'affiche le résultat qu'une fois écrit.
   *
   * ⚠️ En cas d'échec, la case **revient à l'état confirmé**. Laisser une case cochée
   * sur un enregistrement raté montrerait un abonnement qui n'existe pas — le seul
   * défaut de cet écran qui serait aussi une infraction.
   */
  function applyConsent(wanted: boolean, address: string): void {
    if (consentBusy || !shouldRecordConsent(consentState, wanted)) return;
    consentBusy = true;
    consent.disabled = true;

    void recordNewsletterConsent(wanted, address)
      .then((saved) => {
        if (saved) {
          consentState = wanted;
          status.textContent = t(wanted ? 'account.newsletterOn' : 'account.newsletterOff');
          status.classList.remove('is-error');
        } else {
          status.textContent = t('account.newsletterError');
          status.classList.add('is-error');
        }
        consent.checked = consentState;
      })
      .finally(() => {
        consentBusy = false;
        consent.disabled = false;
      });
  }

  /*
    Tant que l'e-mail n'est pas relié, la case attend le formulaire : il n'y aurait
    aucune adresse à consigner, et une preuve sans destinataire n'en est pas une. Une
    fois relié, cocher ou décocher agit immédiatement — c'est le chemin du RETRAIT, qui
    doit rester au moins aussi simple que celui du consentement.
  */
  consent.addEventListener('change', () => {
    if (!isLinked) return;
    void getSupabase()
      .auth.getUser()
      .then(({ data }) => {
        const address = data.user?.email ?? '';
        if (address === '') {
          consent.checked = consentState;
          return;
        }
        applyConsent(consent.checked, address);
      });
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (busy || !isSupabaseConfigured()) return;
    busy = true;
    send.setDisabled(true);

    const redirect = import.meta.env.VITE_SITE_URL ?? window.location.origin;
    // Capturée AVANT l'aller-retour : le champ peut être vidé entre-temps.
    const typed = email.value;

    /*
      `updateUser` et non `signInWithOtp` : le second créerait un NOUVEL utilisateur,
      et la ville anonyme resterait orpheline. L'upgrade en place est tout l'intérêt
      de l'auth anonyme (§8).
    */
    getSupabase()
      .auth.updateUser({ email: email.value }, { emailRedirectTo: redirect })
      .then(({ error }) => {
        status.textContent = error === null ? t('account.linkSent') : t('account.linkError');
        status.classList.toggle('is-error', error !== null);
        if (error === null) {
          form.hidden = true;
          /*
            Le consentement se consigne avec l'adresse SAISIE, et seulement si la case
            était cochée. Un envoi d'e-mail raté n'écrit rien : consigner un
            consentement pour une adresse qui n'a jamais reçu le lien créerait une
            preuve pointant vers un destinataire non vérifié.
          */
          if (consent.checked) applyConsent(true, typed);
        }
      })
      .catch(() => {
        status.textContent = t('account.linkError');
        status.classList.add('is-error');
      })
      .finally(() => {
        busy = false;
        send.setDisabled(false);
      });
  });

  function refresh(): void {
    if (!isSupabaseConfigured()) {
      // Sans backend, la section n'a rien à proposer : elle s'efface plutôt que
      // d'afficher un formulaire qui échouera.
      el.hidden = true;
      return;
    }

    getSupabase()
      .auth.getUser()
      .then(({ data }) => {
        const user = data.user;
        const anonymous = user === null || (user.is_anonymous ?? false);
        const linked = !anonymous && user.email !== undefined && user.email !== '';

        isLinked = linked;

        /*
          Le SEUL chemin qui coche la case sans geste du joueur — parce qu'il ne fait
          que rendre visible un consentement déjà donné, lu sur le serveur.

          ⚠️ **Sous le même drapeau que la case.** Mesuré en production : sans cette
          garde, la requête partait quand même, et PostgREST rend **404** sur une table
          qui n'existe pas encore — quatre lignes rouges dans la console de chaque joueur,
          que le parcours enchaîné tient pour contractuellement vide.
          La leçon est celle de la 0.51.0 : fermer l'ÉCRAN ne ferme pas le COMPORTEMENT.
        */
        if (FEATURES.newsletterConsent) {
          void fetchNewsletterConsent().then((granted) => {
            consentState = granted;
            if (!consentBusy) consent.checked = granted;
          });
        }

        if (linked) {
          status.textContent = t('account.connectedAs', { value: user.email ?? '' });
          status.classList.remove('is-error');
          form.hidden = true;
        } else {
          status.textContent = t('account.anonymousHint');
          form.hidden = false;
        }
      })
      .catch(() => {
        // Session illisible : la section reste sur son état par défaut, sans bruit.
      });
  }

  refresh();

  return {
    el,
    refresh,
    isLinked: () => isLinked,
    destroy() {
      el.remove();
    },
  };
}
